/**
 * Sticky Navigation Component (Refactored)
 * Handles sticky behavior, smooth scrolling, and active state tracking
 */

import { querySelector, querySelectorAll, addEventListener } from '../utils/dom.js';
import { throttleScroll } from '../utils/scroll.js';
import { STICKY_NAV_CONFIG } from '../utils/constants.js';
import { scrollToAnchor } from '../utils/anchorScroll.js';

export class StickyNav {
  constructor(navSelector, options = {}) {
    this.nav = typeof navSelector === 'string' ? querySelector(navSelector) : navSelector;

    if (!this.nav) {
      console.warn(`StickyNav: Navigation element not found for selector "${navSelector}"`);
      return;
    }

    // Configuration
    this.config = {
      ...STICKY_NAV_CONFIG,
      ...options
    };

    // DOM elements
    this.links = querySelectorAll('.jump-link', this.nav);
    this.sections = [];

    // State
    this.navOffset = 0;
    this.isSticky = false;

    // Bind methods
    this.handleScroll = throttleScroll(this.onScroll.bind(this));

    this.init();
  }

  init() {
    if (this.links.length === 0) {
      console.warn('StickyNav: No navigation links found');
      return;
    }

    // Get all section targets
    this.links.forEach(link => {
      const targetId = link.getAttribute('href')?.substring(1);
      if (!targetId) return;

      const targetSection = querySelector(`#${targetId}`);
      if (targetSection) {
        this.sections.push({
          id: targetId,
          element: targetSection,
          link: link
        });
      }
    });

    // Store the original offset position
    this.navOffset = this.nav.offsetTop;

    // Set up event listeners
    this.setupClickHandlers();
    this.setupScrollListener();

    // Initial check
    this.checkStickyPosition();
    this.updateActiveLink();
  }

  setupClickHandlers() {
    // Handle clicks on jump-links within the sticky nav
    // Note: The centralized anchor scroll system handles the actual scrolling
    // We just need to update the active state here
    this.links.forEach(link => {
      addEventListener(link, 'click', (e) => {
        const targetId = link.getAttribute('href')?.substring(1);
        if (!targetId) return;

        // Update active state
        this.setActiveLink(link);
      });
    });
  }

  setupScrollListener() {
    addEventListener(window, 'scroll', this.handleScroll);
  }

  onScroll() {
    this.checkStickyPosition();
    this.updateActiveLink();
  }

  checkStickyPosition() {
    const scrollPosition = window.scrollY;
    const shareSection = querySelector('#share');

    // Check if we've scrolled past the Share section
    let isPastShareSection = false;
    if (shareSection) {
      const shareSectionBottom = shareSection.offsetTop + shareSection.offsetHeight;
      isPastShareSection = scrollPosition > shareSectionBottom;
    }

    // Only make sticky if we're past nav offset AND not past share section
    if (scrollPosition >= this.navOffset && !this.isSticky && !isPastShareSection) {
      this.nav.classList.add(this.config.stickyClass);
      this.isSticky = true;
      // Add spacer to prevent content jump
      this.nav.style.marginBottom = '0';
      document.body.style.paddingTop = this.nav.offsetHeight + 'px';
    } else if ((scrollPosition < this.navOffset || isPastShareSection) && this.isSticky) {
      this.nav.classList.remove(this.config.stickyClass);
      this.isSticky = false;
      document.body.style.paddingTop = '0';
    }
  }

  updateActiveLink() {
    const scrollPosition =
      window.scrollY +
      (this.isSticky ? this.nav.offsetHeight : 0) +
      this.config.observerOffset;

    // Find the current section
    let currentSection = null;

    this.sections.forEach(section => {
      const sectionTop = section.element.offsetTop;
      const sectionBottom = sectionTop + section.element.offsetHeight;

      if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
        currentSection = section;
      }
    });

    // Update active class
    if (currentSection) {
      this.setActiveLink(currentSection.link);
    }
  }

  setActiveLink(activeLink) {
    this.links.forEach(link => {
      link.classList.remove(this.config.activeClass);
    });
    activeLink.classList.add(this.config.activeClass);
  }

  // Clean up when component is destroyed
  destroy() {
    window.removeEventListener('scroll', this.handleScroll);

    this.links.forEach(link => {
      const newLink = link.cloneNode(true);
      if (link.parentNode) {
        link.parentNode.replaceChild(newLink, link);
      }
    });
  }
}
